/**
 * The device's monotonic reading (PROJECT_SPEC.md §8.4 point 3).
 *
 * The server bounds when an offline capture happened by adding the monotonic time elapsed since the last
 * trusted server time, because a user can set a tablet's wall clock to anything but cannot make
 * `performance.now()` run backwards. A browser's `performance.now()` restarts at every page load though, and a
 * PWA is reloaded, killed and restored all the time, so the reading is carried across loads:
 *
 *   monotonic = (reading at last checkpoint) + (wall time since it, never negative) + (performance.now() this load)
 *
 * The wall-clock term only ever ADDS time (a clock wound back adds zero), so tampering can widen the interval
 * the server computes but cannot narrow it. That is the honest direction to be wrong in.
 * ponytail: a browser has no clock that survives a close of the page; an MDM-managed native shell would
 * expose CLOCK_BOOTTIME. The checkpoint is written on a timer and before the page hides to keep the gap small.
 */
import type { KvStore } from "./kv.js";

const KEY = "clock";
const NS_PER_MS = 1_000_000;

export interface MonotonicClock {
  /** Nanoseconds, digits only — the wire format the server expects. */
  nowNs(): string;
  /** Persist the current reading so the next page load can continue from it. */
  checkpoint(): Promise<void>;
}

interface Stored {
  monoMs: number;
  wallMs: number;
}

export async function createClock(
  kv: KvStore,
  perf: { now(): number } = performance,
  wall: () => number = Date.now,
): Promise<MonotonicClock> {
  const stored = (await kv.get(KEY)) as Stored | undefined;
  const carried = stored ? stored.monoMs + Math.max(0, wall() - stored.wallMs) : 0;
  const perfAtLoad = perf.now();
  const monoMs = () => carried + (perf.now() - perfAtLoad);
  return {
    nowNs: () => BigInt(Math.round(monoMs() * NS_PER_MS)).toString(),
    checkpoint: () => kv.put(KEY, { monoMs: monoMs(), wallMs: wall() } satisfies Stored),
  };
}
